import React from "react";
import styles from "./Logos.module.scss";
import { CustomLink } from "../../Global/CustomLink";
import { LogosType } from "@/types/OptionType";
import { passACFLink } from "@/utils/passACFLink";
import { ReactSVG } from "react-svg";
import Image from "next/image";

interface LogosProps {
  logos: LogosType[];
  show: "first" | "second";
}

/**
 * A component to display one or two logos from a list.
 *
 * @param {LogosProps} props - The props for the component.
 * @param {LogosType[]} props.logos - An array of logo objects.
 * @param {'one' | 'two'} [props.show='one'] - Determines whether to show the first or second logo.
 * @returns {JSX.Element} The rendered Logos component.
 */
const Logos = ({ logos, show }: LogosProps) => {
  /**
   * Renders a single logo, handling whether it's an SVG or another image format,
   * and wraps it in a link if one is provided.
   *
   * @param {{ logo: LogosType }} props - The props for the sub-component.
   * @param {LogosType} props.logo - The logo object to render.
   * @returns {JSX.Element} The rendered Logo.
   */
  const Logo = ({ logo }: { logo: LogosType }) => {
    // Determine the JSX for the logo image (SVG or standard Image)
    const logoJSX = logo.logo_positive && (
      <>
        {logo.logo_positive.mime_type === "image/svg+xml" ? (
          <ReactSVG
            src={logo.logo_positive.url}
            wrapper="svg"
            height={40}
            width={110}
            aria-hidden
            beforeInjection={(svg) => {
              svg.setAttribute(
                "alt",
                logo.logo_positive ? logo.logo_positive.alt : "Navbar Logo",
              );
              svg.setAttribute(
                "aria-label",
                logo.logo_positive ? logo.logo_positive.alt : "Navbar Logo",
              );
            }}
          />
        ) : (
          <Image
            width={110}
            height={41}
            src={logo.logo_positive.url}
            priority={true}
            alt={logo.logo_positive.alt}
          />
        )}
      </>
    );

    // Render the logo, wrapping it in a link if available
    return (
      <div className={styles["logo"]}>
        {logo.link ? (
          <CustomLink className={styles["link"]} {...passACFLink(logo, "link")}>
            {logoJSX}
          </CustomLink>
        ) : (
          logoJSX
        )}
      </div>
    );
  };

  return (
    <div className={`${styles["logos"]} logos`}>
      {/* Conditionally render the first logo */}
      {show === "first" && logos[0] && (
        <Logo key={"logo-one"} logo={logos[0]} />
      )}
      {/* Conditionally render the second logo */}
      {show === "second" && logos[1] && (
        <Logo key={"logo-two"} logo={logos[1]} />
      )}
    </div>
  );
};

Logos.displayName = "Logos";

export { Logos };
